'use client';

import { type Editor, type EditorStateSnapshot, useEditorState } from '@tiptap/react';
import { forwardRef, useCallback, useState } from 'react';

// --- Hooks ---
import { useTiptapEditor } from '@/hooks/use-tiptap-editor';
import { isMarkInSchema } from '@/lib/tiptap-utils';

// --- Icons ---
import { ChevronDownIcon } from '@/components/tiptap-editor/tiptap-icons/chevron-down-icon';

// --- UI Primitives ---
import type { ButtonProps } from '@/components/tiptap-editor/tiptap-ui-primitive/button';
import { Button } from '@/components/tiptap-editor/tiptap-ui-primitive/button';
import { Card, CardBody } from '@/components/tiptap-editor/tiptap-ui-primitive/card';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/tiptap-editor/tiptap-ui-primitive/dropdown-menu';

// --- Tiptap UI ---
import { MarkButton } from '@/components/tiptap-editor/tiptap-ui/mark-button';
import { MARK_SHORTCUT_KEYS, markIcons } from '@/components/tiptap-editor/tiptap-ui/mark-button/use-mark';

export interface ScriptDropdownMenuProps extends Omit<ButtonProps, 'type'> {
    /**
     * The Tiptap editor instance.
     */
    editor?: Editor;
    /**
     * Whether to render the dropdown menu in a portal
     * @default false
     */
    portal?: boolean;
    /**
     * Callback for when the dropdown opens or closes
     */
    onOpenChange?: (isOpen: boolean) => void;
}

type ScriptDropdownSnapshot = {
    superscriptInSchema: boolean;
    subscriptInSchema: boolean;
    isActive: boolean;
    currentScript: 'superscript' | 'subscript';
};

function scriptDropdownSnapshotEquality(a: ScriptDropdownSnapshot, b: ScriptDropdownSnapshot | null): boolean {
    return (
        a.superscriptInSchema === b?.superscriptInSchema &&
        a.subscriptInSchema === b?.subscriptInSchema &&
        a.isActive === b?.isActive &&
        a.currentScript === b?.currentScript
    );
}

const scriptDropdownSelector = (ctx: EditorStateSnapshot<Editor | null>): ScriptDropdownSnapshot => {
    const ed = ctx.editor;
    if (!ed) {
        return {
            superscriptInSchema: false,
            subscriptInSchema: false,
            isActive: false,
            currentScript: 'superscript',
        };
    }
    const superscriptInSchema = isMarkInSchema('superscript', ed);
    const subscriptInSchema = isMarkInSchema('subscript', ed);
    const subActive = ed.isActive('subscript');
    const superActive = ed.isActive('superscript');
    return {
        superscriptInSchema,
        subscriptInSchema,
        isActive: !!(superActive || subActive),
        currentScript: subActive ? 'subscript' : 'superscript',
    };
};

export const ScriptDropdownMenu = forwardRef<HTMLButtonElement, ScriptDropdownMenuProps>(
    ({ editor: providedEditor, portal = true, onOpenChange, ...buttonProps }, ref) => {
        const { editor } = useTiptapEditor(providedEditor);
        const [isOpen, setIsOpen] = useState(false);

        const scriptToolbar = useEditorState({
            editor,
            selector: scriptDropdownSelector,
            equalityFn: scriptDropdownSnapshotEquality,
        });

        const superscriptInSchema = scriptToolbar?.superscriptInSchema ?? false;
        const subscriptInSchema = scriptToolbar?.subscriptInSchema ?? false;
        const isActive = scriptToolbar?.isActive ?? false;
        const currentScript = scriptToolbar?.currentScript ?? 'superscript';
        const TriggerIcon = markIcons[currentScript];

        const handleOpenChange = useCallback(
            (open: boolean) => {
                if (!editor) return;
                setIsOpen(open);
                onOpenChange?.(open);
            },
            [editor, onOpenChange],
        );

        if (!editor || !editor.isEditable || (!superscriptInSchema && !subscriptInSchema)) {
            return null;
        }

        return (
            <DropdownMenu
                open={isOpen}
                onOpenChange={handleOpenChange}
            >
                <DropdownMenuTrigger asChild>
                    <Button
                        type="button"
                        data-style="ghost"
                        data-active-state={isActive ? 'on' : 'off'}
                        role="button"
                        tabIndex={-1}
                        aria-label="Superscript / Subscript"
                        tooltip="Superscript / Subscript"
                        {...buttonProps}
                        ref={ref}
                    >
                        <TriggerIcon className="tiptap-button-icon" />
                        <ChevronDownIcon className="tiptap-button-dropdown-small" />
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent portal={portal}>
                    <Card>
                        <CardBody>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.125rem' }}>
                                {superscriptInSchema && (
                                    <DropdownMenuItem asChild>
                                        <MarkButton
                                            editor={editor}
                                            type="superscript"
                                            shortcutKeys={MARK_SHORTCUT_KEYS.superscript}
                                        />
                                    </DropdownMenuItem>
                                )}
                                {subscriptInSchema && (
                                    <DropdownMenuItem asChild>
                                        <MarkButton
                                            editor={editor}
                                            type="subscript"
                                            shortcutKeys={MARK_SHORTCUT_KEYS.subscript}
                                        />
                                    </DropdownMenuItem>
                                )}
                            </div>
                        </CardBody>
                    </Card>
                </DropdownMenuContent>
            </DropdownMenu>
        );
    },
);

ScriptDropdownMenu.displayName = 'ScriptDropdownMenu';

export default ScriptDropdownMenu;
